import React, { useEffect, useState } from "react";
import { Check, CheckCircle2, Loader2, Lock, Ticket } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useCurrency } from "@/lib/currency";

// In-page pricing widget for the AI Commercial Creator add-on.
// States: included (green) · addon_subscription (green) · addon_credits
// (credits counter) · buy_addon (amber pricing banner) · upgrade (tier lock).
export const CommercialAddonBanner = ({ access, onRefresh }) => {
  const [tab, setTab] = useState("subscription");
  const [busy, setBusy] = useState("");
  const { fmt: fx } = useCurrency();

  // Handle the ?addon_checkout=success redirect from Stripe
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const state = q.get("addon_checkout");
    if (!state) return;
    window.history.replaceState({}, "", window.location.pathname);
    if (state === "cancel") { toast.info("Checkout cancelled. No charge was made."); return; }
    const sid = q.get("session_id");
    if (!sid) return;
    api.get(`/commercials/addon/confirm?session_id=${sid}`)
      .then(({ data }) => {
        if (data.applied?.granted_credits) toast.success(`${data.applied.granted_credits} commercial credits added!`);
        else if (data.applied?.subscription_activated || data.applied?.already_processed) toast.success("Commercial Creator add-on is now active — unlimited generations!");
        onRefresh?.();
      })
      .catch(() => toast.error("Couldn't confirm the purchase — it will activate via Stripe shortly."));
  }, [onRefresh]);

  const buy = async (option) => {
    setBusy(option);
    try {
      const { data } = await api.post("/commercials/addon/checkout", {
        option, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : d || "Checkout failed");
      setBusy("");
    }
  };

  if (!access) return null;

  // ── Included in plan (Pro Radio / Network / trial / admin) ────────────────
  if (access.mode === "included") {
    if (access.reason === "admin") return null;
    const planLabel = access.reason === "trial_active" || access.plan === "trial"
      ? "your free trial" : `your ${access.plan === "network" ? "Network" : access.plan === "enterprise" ? "Enterprise" : "Pro Radio"} plan`;
    return (
      <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="cc-addon-included-banner">
        <div className="h-9 w-9 rounded-full bg-emerald-500/15 grid place-items-center shrink-0">
          <CheckCircle2 className="h-5 w-5 text-emerald-300" />
        </div>
        <div>
          <div className="font-display text-sm font-bold text-ink-primary">AI Commercial Creator is included in {planLabel}</div>
          <div className="text-xs text-ink-secondary">Unlimited commercial generations — no add-on purchase needed.</div>
        </div>
      </div>
    );
  }

  // ── Add-on subscription active ────────────────────────────────────────────
  if (access.mode === "addon_subscription") {
    return (
      <div className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="cc-addon-sub-active-banner">
        <div className="h-9 w-9 rounded-full bg-emerald-500/15 grid place-items-center shrink-0">
          <CheckCircle2 className="h-5 w-5 text-emerald-300" />
        </div>
        <div>
          <div className="font-display text-sm font-bold text-ink-primary">Commercial Creator add-on active — unlimited generations</div>
          <div className="text-xs text-ink-secondary">$49/mo standalone add-on. Manage or cancel anytime via Manage billing on the Subscription page.</div>
        </div>
      </div>
    );
  }

  // ── Credit-pack balance ───────────────────────────────────────────────────
  if (access.mode === "addon_credits") {
    return (
      <div className="mt-6 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-4 flex flex-wrap items-center gap-3" data-testid="cc-addon-credits-banner">
        <div className="h-9 w-9 rounded-full bg-brand-amber/15 grid place-items-center shrink-0">
          <Ticket className="h-5 w-5 text-brand-amber" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display text-sm font-bold text-ink-primary">
            <span data-testid="cc-addon-credits-count">{access.addon_credits}</span> commercial generation{access.addon_credits === 1 ? "" : "s"} remaining
          </div>
          <div className="text-xs text-ink-secondary">Each new commercial uses 1 credit — rerolls and voiceovers on it are free. Credits never expire.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => buy("credit_pack")} disabled={!!busy} data-testid="cc-addon-buy-more-pack-btn"
            className="rounded-md border border-brand-amber/50 bg-brand-amber/10 px-3 py-1.5 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 transition disabled:opacity-50">
            {busy === "credit_pack" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Buy 10 more — $${access.pricing.pack_usd}`}
          </button>
          <button onClick={() => buy("subscription")} disabled={!!busy} data-testid="cc-addon-go-unlimited-btn"
            className="rounded-md bg-brand-amber px-3 py-1.5 text-xs font-semibold text-black amber-glow disabled:opacity-50">
            {busy === "subscription" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Go unlimited — $${access.pricing.subscription_usd}/mo`}
          </button>
        </div>
      </div>
    );
  }

  // ── Free / Starter — tier lock, no add-on offer ───────────────────────────
  if (access.mode === "upgrade") {
    return (
      <div className="mt-6 rounded-xl border border-brand-amber/50 bg-brand-amber/5 p-5" data-testid="cc-addon-upgrade-banner">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-brand-amber/15 grid place-items-center shrink-0">
            <Lock className="h-4 w-4 text-brand-amber" />
          </div>
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber mb-1">// Included with Pro Radio</div>
            <div className="font-display text-base font-bold text-ink-primary">AI Commercial Creator needs a bigger plan</div>
            <p className="text-xs text-ink-secondary mt-1 max-w-2xl">
              Ad-spot production is included free with Pro Radio and Network. On Professional it's also available as a standalone add-on.
              Your current plan ({access.plan}) doesn't include it yet.
            </p>
            <a href="/dashboard/billing?tab=plan&plan=pro_radio" data-testid="cc-addon-upgrade-cta"
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black amber-glow">
              Compare plans →
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Professional — full pricing widget (State A) ──────────────────────────
  return (
    <div className="mt-6 rounded-xl border border-brand-amber/50 bg-brand-amber/[0.04] p-5 md:p-6" data-testid="cc-addon-pricing-banner">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-amber mb-1.5">// Add-On · Not included in Professional</div>
      <div className="font-display text-lg font-bold text-ink-primary">Add AI Commercial Creator to your plan</div>
      <p className="text-xs text-ink-secondary mt-1 max-w-2xl">
        Your current plan (Professional) doesn't include Commercial Creator. Add it standalone below — no need to
        upgrade your whole plan — or it's included free if you move up to Pro Radio.
      </p>

      <div className="mt-4 inline-flex rounded-lg border border-line bg-bg-elevated p-1" data-testid="cc-addon-toggle">
        {[["subscription", "Monthly subscription"], ["credit_pack", "Credit pack"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`cc-addon-tab-${id}`}
            className={`rounded-md px-3.5 py-1.5 text-xs font-semibold transition ${
              tab === id ? "bg-brand-amber text-black" : "text-ink-secondary hover:text-ink-primary"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 max-w-3xl">
        <PriceCard
          featured={tab === "subscription"}
          name="Unlimited Subscription"
          price={fx(access.pricing.subscription_usd)} suffix="/mo"
          desc="Best for stations running ads regularly — steady sponsor rotation, weekly spots."
          feats={["Unlimited commercial generations", "3 script variations per brief", "AI TTS voice + mixed spot output", "One-click ship to Imaging Studio"]}
          btnLabel={`Add for ${fx(access.pricing.subscription_usd)}/mo`}
          busy={busy === "subscription"} disabled={!!busy}
          onBuy={() => buy("subscription")}
          testid="cc-addon-buy-subscription-btn"
        />
        <PriceCard
          featured={tab === "credit_pack"}
          name="Credit Pack"
          price={fx(access.pricing.pack_usd)} suffix="one-time"
          desc="Best for occasional or seasonal ad sales — no monthly commitment, credits never expire."
          feats={[`${access.pricing.pack_credits} commercial generations`, "Same script + voice quality as subscription", "Buy more packs anytime"]}
          btnLabel={`Buy ${access.pricing.pack_credits} credits — ${fx(access.pricing.pack_usd)}`}
          busy={busy === "credit_pack"} disabled={!!busy}
          onBuy={() => buy("credit_pack")}
          testid="cc-addon-buy-pack-btn"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border border-line bg-bg-elevated/60 px-3 py-2.5 text-xs text-ink-secondary" data-testid="cc-addon-upsell-note">
        <span>Running more than ~15 spots a month? <span className="text-ink-primary font-medium">Pro Radio</span> includes this free, plus Voice Tracking, Radio Imaging Studio, and the full production suite.</span>
        <a href="/dashboard/billing?tab=plan&plan=pro_radio" className="text-brand-amber font-semibold hover:underline" data-testid="cc-addon-compare-pro-radio-link">
          Compare Pro Radio →
        </a>
      </div>
    </div>
  );
};

const PriceCard = ({ featured, name, price, suffix, desc, feats, btnLabel, busy, disabled, onBuy, testid }) => (
  <div className={`rounded-lg border p-4 flex flex-col ${featured ? "border-brand-amber/60 bg-brand-amber/[0.06]" : "border-line bg-bg-surface"}`}>
    <div className="font-display text-sm font-bold text-ink-primary">{name}</div>
    <div className="mt-1 flex items-baseline gap-1">
      <span className="font-display text-3xl font-black tracking-tight text-ink-primary">{price}</span>
      <span className="text-xs text-ink-muted">{suffix}</span>
    </div>
    <p className="text-[11px] text-ink-secondary mt-1.5">{desc}</p>
    <ul className="mt-3 space-y-1.5 flex-1">
      {feats.map((f) => (
        <li key={f} className="flex items-start gap-1.5 text-xs text-ink-secondary">
          <Check className="h-3.5 w-3.5 text-emerald-400 mt-0.5 shrink-0" />{f}
        </li>
      ))}
    </ul>
    <button onClick={onBuy} disabled={disabled} data-testid={testid}
      className={`mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold transition disabled:opacity-50 ${
        featured ? "bg-brand-amber text-black amber-glow" : "border border-line bg-bg-elevated text-ink-primary hover:border-brand-amber/50"}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
      {btnLabel}
    </button>
  </div>
);
